import { readChatStream } from './stream';
import { ProviderError, type ChatRequest, type ChatResult } from '../types';

interface OpenAIChatChoice {
  index: number;
  message: { role: 'assistant'; content: string | null };
  finish_reason: string;
}

interface OpenAIChatResponse {
  id: string;
  model: string;
  choices: OpenAIChatChoice[];
  usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
}

/**
 * OpenAI Chat Completions. Proxied via Vite (`/api/openai/...`) during dev.
 */
export async function chatOpenAI(
  request: ChatRequest,
  config: { apiKey: string; baseUrl: string },
): Promise<ChatResult> {
  const { messages, opts } = request;
  const augmented = opts.systemPrompt
    ? [{ role: 'system' as const, content: opts.systemPrompt }, ...messages]
    : messages;

  const body = {
    ...(opts.onDelta ? { stream: true, stream_options: { include_usage: true } } : {}),
    model: opts.model ?? 'gpt-4o-mini',
    messages: augmented,
    max_tokens: opts.maxTokens,
    temperature: opts.temperature,
    ...(opts.topP === undefined ? {} : { top_p: opts.topP }),
    ...(opts.stopSequences?.length ? { stop: opts.stopSequences } : {}),
  };

  const res = await fetch(`${config.baseUrl}/v1/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${config.apiKey}`,
    },
    body: JSON.stringify(body),
    signal: opts.signal,
    // Never follow redirects: for the admin-configurable 'custom' base URL this
    // stops a 3xx from pivoting to a cloud-metadata / internal endpoint after
    // the server-side SSRF check has passed.
    redirect: 'error',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ProviderError('openai', res.status, text || res.statusText, text);
  }

  if (opts.onDelta && res.headers.get('content-type')?.includes('text/event-stream')) return readChatStream(res, 'openai', opts.onDelta);

  const data = (await res.json()) as OpenAIChatResponse;
  const text = data.choices[0]?.message.content ?? '';
  return {
    text,
    raw: data,
    // Optional even though OpenAI always sends it: 'custom' gateways reuse this
    // wire format and may not.
    ...(data.usage
      ? { usage: { inputTokens: data.usage.prompt_tokens, outputTokens: data.usage.completion_tokens } }
      : {}),
  };
}
